import { API_BASE_URL } from '../config';
import apiFetch from './apiFetch';

// The PoC console talks to one group of endpoints and unwraps one envelope.
// Keeping that in a single place means a screen never has to remember whether a
// given route answers with `data` or with `data.data`.

const BASE = `${API_BASE_URL}/admin/poc`;

async function request(path, options = {}) {
  const res = await apiFetch(`${BASE}${path}`, {
    ...options,
    headers: options.body ? { 'Content-Type': 'application/json', ...(options.headers || {}) } : options.headers,
  });

  if (res.status === 204) return null;

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body?.error?.message || `HTTP ${res.status}`);
  }
  return body.data;
}

const query = (params) => {
  const usable = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return usable.length ? `?${new URLSearchParams(usable).toString()}` : '';
};

export const pocApi = {
  vocabulary: () => request('/vocabulary'),

  listDatasets: () => request('/datasets'),
  createDataset: (payload) => request('/datasets', { method: 'POST', body: JSON.stringify(payload) }),
  getDataset: (id) => request(`/datasets/${id}`),
  tracks: (datasetId, params) => request(`/datasets/${datasetId}/tracks${query(params || {})}`),
  importTracks: (datasetId, payload) =>
    request(`/datasets/${datasetId}/tracks`, { method: 'POST', body: JSON.stringify(payload) }),
  setTrackVerdict: (trackId, payload) =>
    request(`/tracks/${trackId}/verdict`, { method: 'PUT', body: JSON.stringify(payload) }),

  extractFrames: (id, payload) =>
    request(`/datasets/${id}/extract`, { method: 'POST', body: JSON.stringify(payload) }),
  jobStatus: (jobId) => request(`/jobs/${jobId}`),

  updateDataset: (id, payload) => request(`/datasets/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteDataset: (id) => request(`/datasets/${id}`, { method: 'DELETE' }),

  getFrame: (id) => request(`/frames/${id}`),
  saveBoxes: (frameId, payload) => request(`/frames/${frameId}/boxes`, { method: 'PUT', body: JSON.stringify(payload) }),
  saveConditions: (frameId, conditions) =>
    request(`/frames/${frameId}/conditions`, { method: 'PUT', body: JSON.stringify({ conditions }) }),

  agreement: (datasetId, params) => request(`/datasets/${datasetId}/agreement${query(params)}`),
  metrics: (datasetId, params) => request(`/datasets/${datasetId}/metrics${query(params)}`),

  playPositions: (datasetId) => request(`/datasets/${datasetId}/play-positions`),
  savePlayPositions: (datasetId, playPositions) =>
    request(`/datasets/${datasetId}/play-positions`, { method: 'PUT', body: JSON.stringify({ playPositions }) }),
  deletePlayPosition: (id) => request(`/play-positions/${id}`, { method: 'DELETE' }),
};

// The image URL a frame row carries is relative while the frames live on a
// laptop, and absolute once they are in the bucket. Resolve it against the API
// host rather than the admin's own, which are different ports in development.
export function frameImageURL(imageUrl) {
  if (!imageUrl) return '';
  if (/^https?:\/\//.test(imageUrl)) return imageUrl;
  const apiOrigin = new URL(API_BASE_URL, window.location.origin).origin;
  return `${apiOrigin}${imageUrl}`;
}

export const LABEL_STYLE = {
  // 景品ブロック（ガラス空間1つ ＋ 爪1つ ＋ 操作パネル1つ）。境目が一意に
  // 決まるのはこれだけなので、学習の主クラスであり最初に描くもの。
  block: { color: '#f59e0b', label: '景品ブロック', short: 'B' },
  // キャビネットはブロック 1/2/4 個の親。外周は人によって揺れる。
  cabinet: { color: '#3b82f6', label: 'Cabinet', short: 'C' },
  panel: { color: '#22c55e', label: 'Control Panel', short: 'P' },
};

export const CONDITION_LABELS = {
  frontal: '正面',
  angled: '斜め',
  far: '遠景',
  near: '近景',
  multi_cabinet: '複数台',
  person_occluded: '人物遮蔽',
  fast_pan: '高速パン',
};

// 景品の取り方。**block である条件ではなく属性**。実店舗の30枚中12枚が
// 爪を持たない機械だった（タイミング式・ルーレット式）。
export const MECHANISM_LABELS = {
  claw: '爪でつかむ',
  hook: 'フック・棒に引っ掛ける',
  push: '押して落とす',
  timing: 'タイミングで決まる（レバー・ボタン）',
  roulette: 'ルーレット・スロットの出目',
  other: 'その他',
};

export const SKIP_REASON_LABELS = {
  blur: 'ブレて判断できない',
  too_close: '寄りすぎて台の境界が分からない',
  occluded: '遮蔽されていて判断できない',
  reflection: '反射・映り込みのみ',
  no_panel: 'コントロールパネルが写っていない',
  other: 'その他',
};

// **重複と誤結合は対になっている。**
//
//   重複   … 1台が2本に割れた → 台数が増える
//   誤結合 … 2台が1本に束ねられた → 台数が減る
//
// 重複しか無かったので、台数は減らす方向にしか直せなかった。代表3枚のうち
// 2枚が同じ台で1枚が別の台、という軌跡は実際に出る。「台」にすると2台が
// 1台になり、「誤検出」にすると実在する台が0台になる。
export const TRACK_VERDICTS = [
  { key: 'machine', label: '台', variant: 'success' },
  { key: 'false', label: '誤検出', variant: 'danger' },
  { key: 'duplicate', label: '重複', variant: 'warning' },
  { key: 'merged', label: '誤結合', variant: 'warning' },
];

// 台ではあるが、枠が学習にそのまま使えないもの。
//
// **台数は変わらない。** それでも残すのは、ここが「撮り方を変えるべきか、
// 学習データを足すべきか」を分ける材料になるから。斜めと引きは撮影側、
// 枠のずれとピントは検出器側の話になる。任意入力。
export const BOX_ISSUES = [
  { key: 'oblique', label: '斜めすぎる' },
  { key: 'far', label: '引きが強い' },
  { key: 'box_off', label: '枠がずれている' },
  { key: 'focus_back', label: 'ピントが奥' },
];

// なぜ台ではなかったのか。**「誤検出」だけでは次に何を直せばよいか分からない。**
//
// 実際に一度、76本が実測77台とほぼ一致したのに、目視すると12本がガチャ・
// 7本が人・4本が両替機だった。このとき理由は人の頭の中と会話にしか残らず、
// 「ガチャを台と読む」という**最大の誤り源が数として残らなかった**。
// 理由が集計できれば、次に直すのは学習データか足切りかプロンプトかが決まる。
//
// 自由記述ではなく決まった選択肢にするのは、数えるため。20本の「ガチャ」が
// 「ガチャ機」「ガチャガチャ」「カプセル」に散ると、上位の原因が見えない。
export const FALSE_REASONS = [
  { key: 'gacha', label: 'ガチャ' },
  { key: 'medal', label: 'メダル・スロット' },
  { key: 'person', label: '人' },
  { key: 'pillar', label: '柱・仕切り' },
  { key: 'poster', label: 'ポスター・景品の絵' },
  // 台ではなく、奥にある景品そのものを囲ってしまったもの。ポスターの絵とは
  // 違って**実物**なので、検出器から見て紛らわしさの種類が違う。
  //
  // **「台の中身」と書いてはいけない。** ブロックの枠はガラスの遊技空間＝
  // 景品が入っている場所そのものなので、「中身を囲ったら誤検出」と読めて
  // 規則と逆になる。誤りなのは**台の代わりに景品を囲ったこと**。
  { key: 'prize_inside', label: '奥の景品' },
  { key: 'vending', label: '両替機・自販機' },
  { key: 'unclear', label: '判別できない' },
];

// なぜ2台が1本になったか。
//
// **「見た目が同じ台が並ぶ」は理由にしない。** 同じ筐体が横に並ぶのは
// ゲームセンターの普通の状態で、**それを1台ずつ見分けるのが検出器の仕事**。
// 選択肢に置くと、いちばん重要な失敗を「店のせい」として片付けられてしまう。
// そもそも今の追跡は箱の重なり（位置）だけで結んでいて見た目を見ないので、
// 原因としても成り立たない。
//
// ここに置くのは**こちらが変えられること**だけ。コマ密度・欠落の許容・
// 歩き方の3つに対応する。
export const MERGED_REASONS = [
  { key: 'sparse', label: 'コマが疎で次の台に乗り移った' },
  { key: 'after_occlusion', label: '遮蔽から戻ったら別の台' },
  { key: 'camera_fast', label: 'カメラが速い・振れた' },
];

// 重複の理由は「なぜ1台が2本に割れたか」。これは追跡の設定
// （MinIoU / MaxGapFrames / コマ密度）のどれを触るかに直結する。
export const DUPLICATE_REASONS = [
  { key: 'occluded', label: '遮蔽で切れた' },
  { key: 'video_cut', label: '動画のつなぎ目' },
  { key: 'revisit', label: '同じ台を2度通った' },
  { key: 'sparse', label: 'コマが疎で追えない' },
];

export const MISS_REASON_LABELS = {
  person: '人物',
  reflection: '反射',
  frame_edge: '画面端',
  small_machine: '小型機',
  blur: 'ブレ',
  other: 'その他',
};

export const OCCLUSION_LABELS = {
  none: '遮蔽なし',
  partial: '一部遮蔽',
  full: '全遮蔽',
};

export const percent = (v) => (v === undefined || v === null ? '—' : `${(v * 100).toFixed(1)}%`);
